import { fg } from '@atlaskit/platform-feature-flags';

import { DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI } from '../../../types/default-cloud-graph-document-entity-ati';
import { DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI } from '../../../types/default-cloud-graph-video-entity-ati';
import { type FilterId } from '../../../types/filters';
import { type ThirdPartyConfig } from '../../../types/schemas/types';
import {
	NounSubtypeKey,
	type TypeFilterValue3PEntities,
	TypeFilterValueKey,
} from '../../../utils/filters/universalType';
import { get3PLogo } from '../../icons';
import { ProductResourceTypes3P } from '../types';

import { SharePointIcon, SharepointIconBorderless, SharePointSearchResultIcon } from './icon';

const getSharePointTypeFilterConfig = () => {
	const config = new Map<TypeFilterValueKey, TypeFilterValue3PEntities>();

	config.set(TypeFilterValueKey.Document, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: [
			NounSubtypeKey.Document,
			NounSubtypeKey.Pdf,
			NounSubtypeKey.Page,
			NounSubtypeKey.Blogpost,
		],
	});

	config.set(TypeFilterValueKey.Spreadsheet, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: [NounSubtypeKey.Spreadsheet],
	});

	config.set(TypeFilterValueKey.Presentation, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: [NounSubtypeKey.Presentation],
	});

	config.set(TypeFilterValueKey.Video, {
		entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
		subTypes3P: [NounSubtypeKey.Video],
	});

	config.set(TypeFilterValueKey.Image, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: [NounSubtypeKey.Image],
	});
	config.set(TypeFilterValueKey.Folder, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: [NounSubtypeKey.Folder, NounSubtypeKey.Drive],
	});
	config.set(TypeFilterValueKey.Pdf, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: [NounSubtypeKey.Pdf],
	});
	config.set(TypeFilterValueKey.Notebook, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: [NounSubtypeKey.Notebook],
	});

	config.set(TypeFilterValueKey.WebPage, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: [NounSubtypeKey.WebPage, NounSubtypeKey.Shortcut],
	});
	config.set(TypeFilterValueKey.Other, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: [NounSubtypeKey.Other, NounSubtypeKey.Archive],
	});

	// Comments are embedded within documents as linkedEntities, not standalone entities.
	// Keep the entity type aligned to document results so any consumer of typeFilterConfig still
	// treats SharePoint comment matches as documents rather than standalone comments.
	// SharePoint embedded comments are gated behind a SharePoint-specific flag (independent of the
	// shared 3p flag used for Google Drive) so we can enable/disable SharePoint comment support
	// independently of other 3p products.
	if (fg('rovo_fps_sharepoint_embedded_comments')) {
		config.set(TypeFilterValueKey.Comment, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [],
		});
	}

	return config;
};

export const sharepointConfig: ThirdPartyConfig = {
	displayName: 'Microsoft SharePoint',
	id: 'sharepoint',
	resourceType: ProductResourceTypes3P.Sharepoint,
	icon: get3PLogo(SharePointIcon, 'Sharepoint', 'medium'),
	iconBorderless: get3PLogo(SharepointIconBorderless, 'Sharepoint', 'small'),
	searchResultIcon: get3PLogo(SharePointSearchResultIcon, 'Sharepoint', 'large'),
	integrationAri: 'ari:cloud:platform::integration/microsoft',
	// Use a getter to evaluate lazily so that feature flags are evaluated when accessed, not at module load.
	get typeFilterConfig() {
		return getSharePointTypeFilterConfig();
	},
	get filters(): FilterId[] {
		const baseFilters: FilterId[] = ['lastUpdated', 'onlyTitleMatch', 'space'];
		if (fg('rovo_fps_enable_sharepoint_content_filter')) {
			baseFilters.push('content');
		}
		return baseFilters;
	},
	requiresOauth: true,
	isEnabled: () => true,
};
